/**
 * "Understanding your plan" — the full plain-words explanation, on its own surface.
 *
 * WHY A MODAL AND NOT AN EXPANDER. The plan summary panel carries one or two sentences and must
 * always fit the screen; the full recap runs to several paragraphs plus a before→after row and a
 * reason for each of pension, cashflow and estate. An expander that long would put the scrolling
 * back inside the panel, which is the problem the panel exists to remove (owner, 2026-08-26:
 * "im having to scroll both up and down the screen to see what the hell is going on").
 *
 * The name follows the app's existing voice — the fee page a client meets two screens earlier is
 * "Understanding Aged Care Costs" — so this reads as the same product speaking rather than a new
 * one. Owner's wording, chosen over the first attempt ("Why this plan does what it does").
 *
 * This component is chrome ONLY. Everything inside it is StrategyPlanRecap, unchanged and still the
 * single place that reconciles the plan in words.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { GradientText } from '../../components/ui/index.tsx';

export interface PlanExplanationModalProps {
    /** What is being explained, echoed under the heading so the reader keeps the thread. */
    strategyName?: string | null;
    onClose: () => void;
    children: React.ReactNode;
    /**
     * An action under the explanation — the "Deep dive" into the year-by-year figures lives here
     * now rather than as a fourth button on the card (owner, 2 Oct 2026: three buttons, not five).
     */
    footer?: React.ReactNode;
}

const PlanExplanationModal = ({ strategyName, onClose, children, footer }: PlanExplanationModalProps) => {
    const closeRef = useRef<HTMLButtonElement | null>(null);

    // Escape closes, and opening moves focus into the dialog so a keyboard or screen-reader user is
    // not left behind on the page underneath.
    useEffect(() => {
        closeRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 p-4 sm:p-8"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="plan-explanation-heading"
                className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200/80 flex flex-col max-h-[calc(100vh-4rem)]"
            >
                <div className="flex items-start gap-4 px-6 pt-5 pb-4 border-b-2 border-teal-400/70">
                    <div className="min-w-0">
                        <h2 id="plan-explanation-heading" className="text-2xl font-bold tracking-tight">
                            <GradientText>Understanding your plan</GradientText>
                        </h2>
                        {strategyName && <p className="mt-0.5 text-sm text-slate-500">{strategyName}</p>}
                    </div>
                    <button
                        type="button"
                        ref={closeRef}
                        onClick={onClose}
                        aria-label="Close"
                        className="ml-auto shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    >
                        <X size={20} aria-hidden />
                    </button>
                </div>
                {/* The recap renders its own PlainWordsCard. `-mt-4` cancels that card's top margin,
                    which exists for its old position inline on the page. */}
                <div className="overflow-y-auto px-6 pb-6 pt-2">
                    <div className="[&>*]:mt-0">{children}</div>
                    {footer && <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-4">{footer}</div>}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PlanExplanationModal;
